import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useAuth } from './contexts/AuthContext';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { RoleMessagesProvider } from './contexts/RoleMessagesContext';
import { AccessibilityAnnouncer } from './components/Accessibility/AccessibilityAnnouncer';
import { GlobalRouteObserver } from './components/Accessibility/FocusManager';
import ChatPanel from './components/Chat/ChatPanel';
import LoginPage from './components/Auth/LoginPage';
import RegisterPage from './components/Auth/RegisterPage';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import VerifyEmailSent from './components/Auth/VerifyEmailSent';
import VerifyEmailSuccess from './components/Auth/VerifyEmailSuccess';
import EmailChangeVerified from './components/Auth/EmailChangeVerified';
import Dashboard from './components/Dashboard/Dashboard';
import BoothManagement from './components/BoothManagement/BoothManagement';
import EventManagement from './components/EventManagement/EventManagement';
import BrandingHeaderLogo from './components/Branding/BrandingHeaderLogo';
import UpcomingEvents from './components/Events/UpcomingEvents';
import RegisteredEvents from './components/Events/RegisteredEvents';
import EventDetail from './components/Events/EventDetail';
import RegisteredEventDetail from './components/Events/RegisteredEventDetail';
import RegistrationWizard from './components/Events/RegistrationWizard';
import TermsConditionsList from './components/TermsConditions/TermsConditionsList';
import TermsConditionsForm from './components/TermsConditions/TermsConditionsForm';
import TermsConditionsView from './components/TermsConditions/TermsConditionsView';
import UserManagement from './components/UserManagement/UserManagement';
import JobSeekerManagement from './components/JobSeekerManagement/JobSeekerManagement';
import RegisteredJobSeekerManagement from './components/JobSeekerManagement/RegisteredJobSeekerManagement';
import AISearchPage from './components/JobSeekerManagement/AISearchPage';
import MeetingRecords from './components/MeetingRecords/MeetingRecords';
import MeetingRecordDetail from './components/MeetingRecords/MeetingRecordDetail';
import JobSeekerInterests from './components/JobSeekerInterests/JobSeekerInterests';
import JobSeekerSurvey from './components/JobSeekerSurvey/JobSeekerSurvey';
import JobSeekerQualifications from './components/JobSeekerQualifications/JobSeekerQualifications';
import InterpreterCategories from './components/InterpreterCategories/InterpreterCategories';
import BoothQueueEntry from './components/BoothQueue/BoothQueueEntry';
import BoothQueueWaiting from './components/BoothQueue/BoothQueueWaiting';
import BoothEmployerPage from './components/BoothQueue/BoothEmployerPage';
import BoothQueueManagement from './components/BoothQueue/BoothQueueManagement';
import EmployerPageTemplateEditor from './components/BoothQueue/EmployerPageTemplateEditor';
import VideoCall from './components/VideoCall/VideoCall';
import QueueInviteResolver from './components/BoothQueue/QueueInviteResolver';
import LandingPage from './components/Landing/LandingPage';
import Analytics from './components/Analytics/Analytics';
import NoteManagement from './components/Notes/NoteManagement';
import NoteForm from './components/Notes/NoteForm';
import NoteView from './components/Notes/NoteView';
import NoteViewUser from './components/Notes/NoteViewUser';
import RoleMessageManagement from './components/RoleMessages/RoleMessageManagement';
import OrganizationManagement from './components/OrganizationManagement/OrganizationManagement';
import UserOrgAssignment from './components/UserManagement/UserOrgAssignment';
import RecruiterProfileEditor from './components/Profile/RecruiterProfileEditor';
import CookieConsent from './components/common/CookieConsent';
import LegalPageEditor from './components/Legal/LegalPageEditor';
import LegalPageView from './components/Legal/LegalPageView';
import FooterTextEditor from './components/Footer/FooterTextEditor';
import ResumeBuilderLimitsEditor from './components/ResumeBuilder/ResumeBuilderLimitsEditor';
import ChatPermissionsEditor from './components/Chat/ChatPermissionsEditor';
import SystemLogs from './components/SystemLogs/SystemLogs';
import LandingPageEditor from './components/LandingPageEditor/LandingPageEditor';
import DualHorizontalScrollManager from './components/UI/DualHorizontalScrollManager';
import AppFooter from './components/Layout/AppFooter';
import './App.css';

// Component to conditionally render header and footer
const AppLayout = ({ children }) => {
    const { user } = useAuth();
    const location = useLocation();

    const hideFooter = location.pathname.startsWith('/booth-queue/')
        || location.pathname.startsWith('/video-call/')
        || location.pathname.startsWith('/legal/');

    // Roles that should have access to Team Chat
    const chatEnabledRoles = new Set(
        ['Recruiter', 'BoothAdmin', 'Support', 'GlobalSupport', 'Interpreter', 'GlobalInterpreter']
            .map(role => role.toLowerCase())
    );
    const showChat = user && user.role && chatEnabledRoles.has(user.role.toLowerCase());

    return (
        <div className="App">
            {/* Must be first in DOM so Tab from the focused live region lands on the skip link */}
            <GlobalRouteObserver />
            {children}

            {/* Show ChatPanel for authenticated users with chat-enabled roles */}
            {showChat && <ChatPanel />}
            <DualHorizontalScrollManager />

            <CookieConsent />
            <AccessibilityAnnouncer />
            {!hideFooter && <AppFooter />}
        </div>
    );
};

function App() {
    const RequireAuth = ({ children }) => {
        const { user, loading } = useAuth();
        const location = useLocation();
        
        if (loading) return null; // could render a spinner if desired
        
        if (!user) {
            // Check if this is a queue invite link - store slug (+ optional /<event> or ?event=) for redirect after login
            if (location.pathname.startsWith('/queue/')) {
                const inviteSlug = location.pathname.replace('/queue/', '');
                const pending = `${inviteSlug}${location.search || ''}`;
                localStorage.setItem('pendingQueueInvite', pending);
            }
            // Pass the original location so login can redirect back (include query for event-specific links)
            const returnTo = `${location.pathname}${location.search || ''}`;
            return <Navigate to={`/login?redirect=${encodeURIComponent(returnTo)}`} replace />;
        }
        return children;
    };

    // Route that shows full JobSeekerManagement for SuperAdmin, and RegisteredJobSeekerManagement for Admin
    const JobSeekerManagementRouter = () => {
        const { user } = useAuth();
        if (user?.role === 'SuperAdmin') return <JobSeekerManagement />;
        if (user?.role === 'Admin' || user?.role === 'AdminEvent') return <RegisteredJobSeekerManagement />;
        return <JobSeekerManagement />;
    };

    const RequireRole = ({ children, allowedRoles }) => {
        const { user, loading } = useAuth();
        const location = useLocation();
        
        if (loading) return null;
        
        if (!user) {
            return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
        }
        
        if (allowedRoles && !allowedRoles.includes(user.role)) {
            // Redirect unauthorized users to dashboard
            return <Navigate to="/dashboard" replace />;
        }
        
        return children;
    };
    return (
        <HelmetProvider>
        <ThemeProvider>
            <AuthProvider>
                <RoleMessagesProvider>
                    <SocketProvider>
                        <ToastProvider>
                        <Router>
                            <AppLayout>
                                <Routes>
                                    <Route path="/" element={<LandingPage />} />
                                    <Route path="/login" element={<LoginPage />} />
                                    <Route path="/register" element={<RegisterPage />} />
                                    <Route path="/forgot-password" element={<ForgotPassword />} />
                                    <Route path="/reset-password" element={<ResetPassword />} />
                                    <Route path="/verify-email-sent" element={<VerifyEmailSent />} />
                                    <Route path="/email-verified" element={<VerifyEmailSuccess />} />
                                    <Route path="/email-change-verified" element={<EmailChangeVerified />} />
                                    <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
                                    <Route path="/dashboard/my-account" element={<RequireAuth><Dashboard /></RequireAuth>} />
                                    <Route path="/dashboard/survey" element={<RequireAuth><Dashboard /></RequireAuth>} />
                                    <Route path="/dashboard/camera-settings" element={<RequireAuth><Dashboard /></RequireAuth>} />
                                    <Route path="/dashboard/edit-profile" element={<RequireAuth><Dashboard /></RequireAuth>} />
                                    <Route path="/dashboard/view-profile" element={<RequireAuth><Dashboard /></RequireAuth>} />
                                    <Route path="/dashboard/delete-account" element={<RequireAuth><Dashboard /></RequireAuth>} />
                                    <Route path="/dashboard/resume-builder" element={<RequireAuth><Dashboard /></RequireAuth>} />
                                    <Route path="/boothmanagement" element={<RequireAuth><RequireRole allowedRoles={['Admin', 'AdminEvent', 'GlobalSupport']}><BoothManagement /></RequireRole></RequireAuth>} />
                                    <Route path="/boothmanagement/template-editor" element={<RequireAuth><RequireRole allowedRoles={['Admin', 'AdminEvent', 'GlobalSupport']}><EmployerPageTemplateEditor /></RequireRole></RequireAuth>} />
                                    <Route path="/eventmanagement" element={<RequireAuth><EventManagement /></RequireAuth>} />
                                    <Route
                                        path="/branding"
                                        element={
                                            <RequireAuth>
                                                <RequireRole allowedRoles={['Admin', 'GlobalSupport', 'SuperAdmin']}>
                                                    <BrandingHeaderLogo />
                                                </RequireRole>
                                            </RequireAuth>
                                        }
                                    />
                                    <Route path="/usermanagement" element={<RequireAuth><UserManagement /></RequireAuth>} />
                                    <Route path="/jobseekermanagement" element={
                                        <RequireAuth>
                                            {/* SuperAdmin sees all job seekers; Admin sees only their org's registered job seekers */}
                                            <JobSeekerManagementRouter />
                                        </RequireAuth>
                                    } />
                                    <Route path="/meeting-records" element={<RequireAuth><MeetingRecords /></RequireAuth>} />
                                    <Route path="/meeting-records/:id" element={<RequireAuth><MeetingRecordDetail /></RequireAuth>} />
                                    <Route
                                        path="/recruiter-ai-search"
                                        element={
                                            <RequireAuth>
                                                <Navigate to="/meeting-records" replace />
                                            </RequireAuth>
                                        }
                                    />
                                    <Route path="/jobseeker-interests" element={<RequireAuth><JobSeekerInterests /></RequireAuth>} />
                                    <Route
                                        path="/jobseeker-survey"
                                        element={
                                            <RequireAuth>
                                                <RequireRole allowedRoles={['Admin', 'GlobalSupport', 'SuperAdmin']}>
                                                    <JobSeekerSurvey />
                                                </RequireRole>
                                            </RequireAuth>
                                        }
                                    />
                                    <Route path="/jobseeker-qualifications" element={<RequireAuth><JobSeekerQualifications /></RequireAuth>} />
                                    {/* Job Seeker event flow */}
                                    <Route path="/events/upcoming" element={<RequireAuth><UpcomingEvents /></RequireAuth>} />
                                    <Route path="/events/registered" element={<RequireAuth><RegisteredEvents /></RequireAuth>} />
                                    <Route path="/events/registered/:slug" element={<RequireAuth><RegisteredEventDetail /></RequireAuth>} />
                                    <Route path="/event/:slug" element={<RequireAuth><EventDetail /></RequireAuth>} />
                                    <Route path="/event/:slug/register" element={<RegistrationWizard />} />
                                    <Route path="/terms-conditions" element={<RequireAuth><TermsConditionsList /></RequireAuth>} />
                                    <Route path="/terms-conditions/create" element={<RequireAuth><TermsConditionsForm /></RequireAuth>} />
                                    <Route path="/terms-conditions/:id" element={<RequireAuth><TermsConditionsView /></RequireAuth>} />
                                    <Route path="/terms-conditions/:id/edit" element={<RequireAuth><TermsConditionsForm /></RequireAuth>} />
                                    <Route path="/interpreter-categories" element={<RequireAuth><InterpreterCategories /></RequireAuth>} />
                                    <Route path="/analytics" element={<RequireAuth><Analytics /></RequireAuth>} />
                                    {/* Notes Routes */}
                                    <Route path="/notes" element={<RequireAuth><NoteManagement /></RequireAuth>} />
                                    <Route path="/notes/create" element={<RequireAuth><NoteForm /></RequireAuth>} />
                                    <Route path="/notes/:id" element={<RequireAuth><NoteView /></RequireAuth>} />
                                    <Route path="/notes/:id/edit" element={<RequireAuth><NoteForm /></RequireAuth>} />
                                    <Route path="/troubleshooting" element={<RequireAuth><NoteViewUser type="troubleshooting" /></RequireAuth>} />
                                    <Route path="/instructions" element={<RequireAuth><NoteViewUser type="instruction" /></RequireAuth>} />
                                    <Route path="/role-messages" element={<RequireAuth><RoleMessageManagement /></RequireAuth>} />
                                    {/* Organization Management (SuperAdmin) */}
                                    <Route path="/organizations" element={<RequireAuth><RequireRole allowedRoles={['SuperAdmin']}><OrganizationManagement /></RequireRole></RequireAuth>} />
                                    <Route path="/org-users" element={<RequireAuth><RequireRole allowedRoles={['SuperAdmin']}><UserOrgAssignment /></RequireRole></RequireAuth>} />
                                    <Route
                                        path="/ai-search"
                                        element={
                                            <RequireAuth>
                                                <RequireRole allowedRoles={['SuperAdmin', 'Admin', 'AdminEvent', 'GlobalSupport']}>
                                                    <AISearchPage />
                                                </RequireRole>
                                            </RequireAuth>
                                        }
                                    />
                                    <Route
                                        path="/recruiter-profile"
                                        element={
                                            <RequireAuth>
                                                <RequireRole allowedRoles={['Recruiter', 'BoothAdmin']}>
                                                    <RecruiterProfileEditor />
                                                </RequireRole>
                                            </RequireAuth>
                                        }
                                    />
                                    {/* Booth Queue Routes */}
                                    <Route path="/queue/:inviteSlug" element={<RequireAuth><QueueInviteResolver /></RequireAuth>} />
                                    <Route path="/queue/:inviteSlug/:eventCode" element={<RequireAuth><QueueInviteResolver /></RequireAuth>} />
                                    <Route path="/queue/:inviteSlug/test/:testCode" element={<RequireAuth><QueueInviteResolver /></RequireAuth>} />
                                    <Route path="/booth-queue/:eventSlug/:boothId" element={<RequireAuth><BoothEmployerPage /></RequireAuth>} />
                                    <Route path="/booth-queue/:eventSlug/:boothId/entry" element={<RequireAuth><BoothQueueEntry /></RequireAuth>} />
                                    <Route path="/booth-queue/:eventSlug/:boothId/waiting" element={<RequireAuth><BoothQueueWaiting /></RequireAuth>} />
                                    <Route path="/booth-queue/manage/:boothId" element={<RequireAuth><BoothQueueManagement /></RequireAuth>} />
                                    <Route path="/video-call/:callId" element={<RequireAuth><VideoCall /></RequireAuth>} />
                                    {/* Legal Pages — public view */}
                                    <Route path="/legal/terms-of-use" element={<LegalPageView type="terms-of-use" />} />
                                    <Route path="/legal/privacy-policy" element={<LegalPageView type="privacy-policy" />} />
                                    {/* Legal Pages — SuperAdmin editor */}
                                    {/* Public home page editor — SuperAdmin only (Sprint 7 "Home Template") */}
                                    <Route path="/home-page-editor" element={<RequireAuth><RequireRole allowedRoles={['SuperAdmin']}><LandingPageEditor /></RequireRole></RequireAuth>} />
                                    <Route path="/footer-text" element={<RequireAuth><RequireRole allowedRoles={['SuperAdmin']}><FooterTextEditor /></RequireRole></RequireAuth>} />
                                    <Route path="/resume-builder-limits" element={<RequireAuth><RequireRole allowedRoles={['SuperAdmin']}><ResumeBuilderLimitsEditor /></RequireRole></RequireAuth>} />
                                    <Route path="/chat-permissions" element={<RequireAuth><RequireRole allowedRoles={['SuperAdmin']}><ChatPermissionsEditor /></RequireRole></RequireAuth>} />
                                    {/* System logs — SuperAdmin only (Sprint 7) */}
                                    <Route path="/logs" element={<Navigate to="/logs/activity" replace />} />
                                    <Route path="/logs/activity" element={<RequireAuth><RequireRole allowedRoles={['SuperAdmin']}><SystemLogs key="activity" type="activity" /></RequireRole></RequireAuth>} />
                                    <Route path="/logs/errors" element={<RequireAuth><RequireRole allowedRoles={['SuperAdmin']}><SystemLogs key="errors" type="errors" /></RequireRole></RequireAuth>} />
                                    <Route path="/legal/terms-of-use/edit" element={<RequireAuth><RequireRole allowedRoles={['SuperAdmin']}><LegalPageEditor type="terms-of-use" /></RequireRole></RequireAuth>} />
                                    <Route path="/legal/privacy-policy/edit" element={<RequireAuth><RequireRole allowedRoles={['SuperAdmin']}><LegalPageEditor type="privacy-policy" /></RequireRole></RequireAuth>} />
                                </Routes>
                            </AppLayout>
                        </Router>
                        </ToastProvider>
                    </SocketProvider>
                </RoleMessagesProvider>
            </AuthProvider>
        </ThemeProvider>
        </HelmetProvider>
    );
}

export default App;
